import { FeedbacksTable } from '@/components/feedbacks/feedbacks-table.js';
import { InfoPage } from '@/components/info-page.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu.js';
import {
  coachesTypeaheadSearch,
  lessonTypeaheadSearch,
  useAllSchools,
  useCoachById,
  useLessonById,
} from '@/lib/api-call-hooks.js';
import { EmptyArray, getSchool, getSessionFeedbacks } from '@/lib/api-calls.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { buildClientExportColumns, exportToCSV } from '@/lib/csv-utils.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { BulkUpdateLateFeedback } from '@/routes/_layout/sessions/-components/bulk-update-late-feedback.js';
import { BulkUpdateStudentAttendance } from '@/routes/_layout/sessions/-components/bulk-update-student-attended.js';
import { FrequentTimeZones, isAirAdmin, isNotNullish, PrismaEnums } from '@airreading/common';
import {
  GetSessionFeedbacksModifier,
  SessionFeedbackWithAssessment,
  studentYearOption,
  toSessionFeedbackCompoundId,
} from '@airreading/common/data';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { SchoolPageTitle } from '../-components/school-page-title.js';
import { schoolDetailsTabs } from '../-components/tabs.js';
import { getFeedbacksTableOverrides } from './-generate-session-attendance-overrides.js';
import { validateRouteRole } from '@/lib/routing.js';
import { feedbacksPageColumns } from '@/routes/_layout/sessions/-components/feedbacks-page-columns.js';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/session-attendance')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolSessionAttendanceDetails,
  validateSearch: paginationRequestSchema.extend({
    feedbackSubmitted: z.boolean().optional(),
    studentAttended: z.boolean().optional(),
    coachId: z.string().optional(),
    lessonIds: z.string().array().optional(),
    grades: z.array(z.nativeEnum(PrismaEnums.StudentYear)).optional(),
    query: z.string().optional(),
    startOnOrAfter: z.string().optional(),
    endOnOrBefore: z.string().optional(),
  }),
});

function SchoolSessionAttendanceDetails() {
  const { schoolId } = Route.useParams();
  const { data: schoolData } = useSuspenseQuery(getSchool(schoolId));
  const { data: allSchools } = useAllSchools();
  const schoolIdToDistrictName = Object.fromEntries(allSchools.map((school) => [school.id, school.org.name]));
  const [isSelectAllPagesActive, setIsSelectAllPagesActive] = useState(false);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      grades: [[grades, activeGrades], setGrades],
      feedbackSubmitted: [[feedbackSubmitted, activeFeedbackSubmitted], setFeedbackSubmitted],
      studentAttended: [[studentAttended, activeStudentAttended], setStudentAttended],
      coachId: [[coachId, activeCoachId], setSelectedCoachId],
      lessonIds: [[selectedLessonIds, activeLessonIds], setLessonIds],
      startOnOrAfter: [[startOnOrAfter, activeStartOnOrAfter], setStartOnOrAfter],
      endOnOrBefore: [[endOnOrBefore, activeEndOnOrBefore], setEndOnOrBefore],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, [
    'query',
    'grades',
    'feedbackSubmitted',
    'studentAttended',
    'coachId',
    'lessonIds',
    'startOnOrAfter',
    'endOnOrBefore',
  ]);

  const requestQuery = {
    pageIndex,
    pageSize,
    isAllPages: false,
    filters: {
      feedbackSubmitted: activeFeedbackSubmitted,
      coachId: activeCoachId,
      lessonId: activeLessonIds?.[0],
      query: activeQuery,
      grades: activeGrades,
      schoolIds: [schoolId], // Fixed parameter from route
      studentAttended: activeStudentAttended,
      startOnOrAfterPlainDateInUSCentral: activeStartOnOrAfter,
      startOnOrBeforePlainDateInUSCentral: activeEndOnOrBefore,
    },
  } satisfies GetSessionFeedbacksModifier;

  const {
    data: sessionFeedbacksResp,
    isPending: isLoadingSessionFeedbacks,
    error,
  } = useQuery(getSessionFeedbacks(requestQuery));

  // Lazy query implementation - only executes when refetchAllSessionFeedbacks is called
  const { refetch: refetchAllSessionFeedbacks } = useQuery({
    ...getSessionFeedbacks({
      pageIndex: 0,
      pageSize: 0,
      filters: requestQuery.filters,
      isAllPages: true,
    }),
    enabled: false, // This prevents automatic execution
  });

  const sessionFeedbacks = useMemo(() => sessionFeedbacksResp?.sessionFeedbacks || EmptyArray, [sessionFeedbacksResp]);
  const queryClient = useQueryClient();

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<SessionFeedbackWithAssessment>({
      getRowId: (row) => toSessionFeedbackCompoundId(row),
      rowIdToData: (id) => sessionFeedbacks.find((feedback) => toSessionFeedbackCompoundId(feedback) === id),
    });

  const fetchLessonById = useLessonById();
  const fetchCoachById = useCoachById();

  // Handle isSelectAllPagesActive changes and reset selections when deselecting all
  const handleSelectAllPagesChange = useCallback(
    (isActive: boolean) => {
      setIsSelectAllPagesActive(isActive);
      if (!isActive) {
        // When deselecting all pages, reset all row selections
        resetAllRowSelection();
      }
    },
    [resetAllRowSelection],
  );

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search sessions by student name or email"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={startOnOrAfter}
          toPlainDateString={endOnOrBefore}
          onRangeSelect={(range) => {
            setStartOnOrAfter(range.fromPlainDateString);
            setEndOnOrBefore(range.toPlainDateString);
          }}
          placeholder="Filter by date range"
        />
        <TableFilterDropdown
          title="Feedback submitted?"
          singleSelect
          options={[
            { value: true, label: 'Submitted' },
            { value: false, label: 'Not submitted' },
          ]}
          selectedValues={[feedbackSubmitted].filter(isNotNullish)}
          onChange={(selected) => {
            setFeedbackSubmitted(selected.filter((v) => v !== feedbackSubmitted)[0]);
          }}
        />
        <TableFilterDropdown
          title="Lesson"
          options={lessonTypeaheadSearch(queryClient)}
          selectedValues={selectedLessonIds ?? []}
          onChange={setLessonIds}
          singleSelect
          getOptionSearchFn={fetchLessonById}
        />
        <TableFilterDropdown
          title="Teacher"
          options={coachesTypeaheadSearch(queryClient)}
          singleSelect={true}
          selectedValues={coachId ? [coachId] : []}
          onChange={([newId]) => {
            setSelectedCoachId(newId);
          }}
          getOptionSearchFn={fetchCoachById}
        />
        <TableFilterDropdown
          title="Student grade"
          options={Object.values(PrismaEnums.StudentYear).map((grade) => ({
            label: studentYearOption(grade),
            value: grade,
          }))}
          selectedValues={grades ?? []}
          onChange={setGrades}
        />
        <TableFilterDropdown
          title="Attendance"
          singleSelect
          options={[
            { value: true, label: 'Attended' },
            { value: false, label: 'Did not attend' },
          ]}
          selectedValues={[studentAttended].filter(isNotNullish)}
          onChange={(selected) => {
            setStudentAttended(selected.filter((v) => v !== studentAttended)[0]);
          }}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      startOnOrAfter,
      endOnOrBefore,
      setStartOnOrAfter,
      setEndOnOrBefore,
      feedbackSubmitted,
      queryClient,
      selectedLessonIds,
      setLessonIds,
      fetchLessonById,
      coachId,
      grades,
      setGrades,
      studentAttended,
      resetFilters,
      setQuery,
      setFeedbackSubmitted,
      setSelectedCoachId,
      setStudentAttended,
      fetchCoachById,
    ],
  );

  const columns = useMemo(
    () =>
      feedbacksPageColumns(
        FrequentTimeZones.UsCentral,
        schoolIdToDistrictName,
        true,
        isSelectAllPagesActive,
        handleSelectAllPagesChange,
      ),
    [schoolIdToDistrictName, isSelectAllPagesActive, handleSelectAllPagesChange],
  );
  //get row count
  const totalRowCount = useMemo(() => sessionFeedbacksResp?.total || 0, [sessionFeedbacksResp]);

  const exportColumns = useMemo(
    () =>
      buildClientExportColumns(
        columns.filter((col) => col.id !== 'viewDetailsAdmin'),
        getFeedbacksTableOverrides(schoolIdToDistrictName),
      ),
    [columns, schoolIdToDistrictName],
  );

  // Handle export functionality
  const handleExportSessionFeedbacks = useCallback(async () => {
    const date = new Date().toISOString().split('T')[0];

    if (isSelectAllPagesActive) {
      // Export all session feedbacks with filters applied (without pagination)
      const result = await refetchAllSessionFeedbacks();
      const allSessionFeedbacks = result.data?.sessionFeedbacks || [];
      exportToCSV(allSessionFeedbacks, exportColumns, `session_attendance_export_${date}.csv`);
    } else {
      // Export only selected rows from current page
      exportToCSV(
        rowDataSelection,
        exportColumns,
        `${schoolData.name.replace(/ /g, '_')}_session_attendance_export_${date}.csv`,
      );
    }
  }, [isSelectAllPagesActive, refetchAllSessionFeedbacks, exportColumns, rowDataSelection, schoolData.name]);

  const bulkActions = useMemo(
    () => (
      <>
        <BulkUpdateStudentAttendance
          key={'cancel-attendance'}
          selectedFeedbacks={rowDataSelection}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Cancel student attendance</DropdownMenuItem>}
          attended={false}
          onSuccess={resetAllRowSelection}
          isSelectAllPagesActive={isSelectAllPagesActive}
          filters={requestQuery.filters}
        />
        <BulkUpdateStudentAttendance
          key={'mark-attendance'}
          selectedFeedbacks={rowDataSelection}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Mark student as attended</DropdownMenuItem>}
          attended={true}
          onSuccess={resetAllRowSelection}
          isSelectAllPagesActive={isSelectAllPagesActive}
          filters={requestQuery.filters}
        />
        <BulkUpdateLateFeedback
          lateFeedbackDismissed={true}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Dismiss late feedback tag</DropdownMenuItem>}
          selectedFeedbacks={rowDataSelection}
          onSuccess={resetAllRowSelection}
          isSelectAllPagesActive={isSelectAllPagesActive}
          filters={requestQuery.filters}
        />
        <DropdownMenuItem
          onSelect={async (e) => {
            e.preventDefault();
            await handleExportSessionFeedbacks();
          }}
        >
          Export student feedback
        </DropdownMenuItem>
      </>
    ),
    [
      rowDataSelection,
      handleExportSessionFeedbacks,
      resetAllRowSelection,
      isSelectAllPagesActive,
      requestQuery.filters,
    ],
  );

  // Select all rows on current page when isSelectAllPagesActive is true and students change
  useEffect(() => {
    if (isSelectAllPagesActive && sessionFeedbacks.length > 0) {
      const allRowIds = sessionFeedbacks.reduce(
        (acc, sessionFeedback) => {
          acc[toSessionFeedbackCompoundId(sessionFeedback)] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      onRowSelectionChange(allRowIds);
    }
  }, [sessionFeedbacks, isSelectAllPagesActive]);

  // Reset isSelectAllPagesActive when filters change
  useEffect(() => {
    if (isSelectAllPagesActive) {
      setIsSelectAllPagesActive(false);
      resetAllRowSelection();
    }
  }, [
    activeQuery,
    activeGrades,
    activeFeedbackSubmitted,
    activeStudentAttended,
    activeCoachId,
    activeLessonIds,
    activeStartOnOrAfter,
    activeEndOnOrBefore,
  ]);

  if (error) {
    return (
      <InfoPage
        headerText={
          <SchoolPageTitle name={schoolData.name || schoolData.id} isHighPriority={schoolData.isHighPriority} />
        }
        breadcrumbs={{
          linkItems: [
            {
              label: 'Schools',
              link: {
                to: '/orgs/schools',
              },
            },
          ],
          currentPage: schoolData.name,
        }}
        tabs={schoolDetailsTabs({ schoolId })}
      >
        <div>Failed to fetch session attendance data. Please try again or notify Air Reading.</div>
      </InfoPage>
    );
  }

  return (
    <InfoPage
      headerText={
        <SchoolPageTitle name={schoolData.name || schoolData.id} isHighPriority={schoolData.isHighPriority} />
      }
      breadcrumbs={{
        linkItems: [
          {
            label: 'Schools',
            link: {
              to: '/orgs/schools',
            },
          },
        ],
        currentPage: schoolData.name,
      }}
      tabs={schoolDetailsTabs({ schoolId })}
    >
      <FeedbacksTable
        sessionFeedbacks={sessionFeedbacks}
        isLoading={isLoadingSessionFeedbacks}
        tableHookProps={{
          state: {
            rowSelection,
            pagination: {
              pageIndex,
              pageSize,
            },
          },
          onPaginationChange,
          onRowSelectionChange,
          getRowId,
          rowCount: sessionFeedbacksResp?.total || 0,
        }}
        filters={{
          filterLeftComponents,
        }}
        bulkActionsMenuItems={bulkActions}
        isSelectAllPagesActive={isSelectAllPagesActive}
        onSelectAllPagesChange={handleSelectAllPagesChange}
        totalRowCount={totalRowCount}
      />
    </InfoPage>
  );
}
